'use client';

import React, { useState, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Upload, FileText } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { EmailForm } from '@/components/email-form';

interface DragDropUploadProps {
  onFileUpload?: (file: File, email: string) => void;
}

export function DragDropUpload({ onFileUpload }: DragDropUploadProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isValidType, setIsValidType] = useState(true);
  const [fileName, setFileName] = useState('');
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [email, setEmail] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.types[0] !== 'Files') return;
    setIsDragOver(true);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      handleFile(droppedFile);
    }
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      handleFile(selectedFile);
    }
  };

  const handleEmailSubmit = (email: string, fileName: string) => {
    setEmail(email);

    // Subir archivo con email
    if (file) {
      handleFileWithEmail(file, email);
    }
  };

  const handleFileWithEmail = async (selectedFile: File, email: string) => {
    setFile(selectedFile);
    setFileName(selectedFile.name);

    // Validar tipo de archivo para evitar subida de archivos peligrosos
    const dangerousExtensions = [
      '.exe', '.bat', '.cmd', '.com', '.pif', '.scr', '.vbs', '.js', '.jse',
      '.wsf', '.wsh', '.msi', '.msp', '.mst', '.inf', '.reg', '.pif', '.sh',
      '.bash', '.pl', '.php', '.py', '.rb', '.sql', '.jar', '.vbe', '.vba',
      '.doc', '.xls', '.ppt', '.pps', '.pptx', '.docm', '.xlsm', '.pptm', '.ppsm'
    ];

    const fileExtension = '.' + selectedFile.name.split('.').pop()?.toLowerCase();
    const isDangerous = dangerousExtensions.includes(fileExtension);

    if (isDangerous) {
      setIsValidType(false);
      return; // No permitir la subida de archivos peligrosos
    }

    setIsValidType(true);

    // Iniciar la barra de progreso automáticamente
    setIsUploading(true);
    setProgress(0);

    try {
      // Calcular incremento por paso para completar en 2.2 segundos
      // 2200ms / 11 pasos = 200ms por paso con incrementos de 10% cada vez
      let currentProgress = 0;
      const step = 10; // 10% por paso
      const stepDuration = 200; // 200ms por paso (2200ms / 11 pasos)

      const interval = setInterval(() => {
        currentProgress += step;
        if (currentProgress >= 100) {
          clearInterval(interval);
          setProgress(100);
        } else {
          setProgress(currentProgress);
        }
      }, stepDuration);

      // Esperar a que se complete la barra de progreso
      await new Promise(resolve => setTimeout(resolve, 2200));

      // Crear FormData y enviar archivo y email al servidor
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('email', email);

      const response = await fetch('/api/upload', {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Archivo procesado exitosamente con LlamaParse:', result);
        setUploadedFileName(result.originalFileName);

        // Llamar a la función de callback si existe
        if (onFileUpload) {
          onFileUpload(selectedFile, email);
        }
      } else {
        // Comprobar si la respuesta es JSON o no
        let error;
        try {
          error = await response.json();
        } catch (jsonError) {
          // Si no es JSON válido, obtener el texto plano
          const errorText = await response.text();
          error = { details: errorText, error: 'Error de formato inesperado' };
          console.error('La respuesta no es JSON válido:', errorText);
        }

        console.error('Error al procesar archivo:', error);

        // Mostrar mensaje de error al usuario
        alert(`Error al procesar el archivo: ${error.details || error.error || 'Error desconocido'}`);
      }
    } catch (error) {
      console.error('Error durante el procesamiento:', error);
    }

    setIsUploading(false);
  };

  const handleFile = async (selectedFile: File) => {
    // Validar tipo de archivo para evitar subida de archivos peligrosos
    const dangerousExtensions = [
      '.exe', '.bat', '.cmd', '.com', '.pif', '.scr', '.vbs', '.js', '.jse',
      '.wsf', '.wsh', '.msi', '.msp', '.mst', '.inf', '.reg', '.pif', '.sh',
      '.bash', '.pl', '.php', '.py', '.rb', '.sql', '.jar', '.vbe', '.vba',
      '.doc', '.xls', '.ppt', '.pps', '.pptx', '.docm', '.xlsm', '.pptm', '.ppsm'
    ];

    const fileExtension = '.' + selectedFile.name.split('.').pop()?.toLowerCase();
    const isDangerous = dangerousExtensions.includes(fileExtension);

    if (isDangerous) {
      setIsValidType(false);
      setFileName(selectedFile.name);
      return; // No permitir la subida de archivos peligrosos
    }

    setFile(selectedFile);
    setFileName(selectedFile.name);
    setIsValidType(true);

    // Iniciar la barra de progreso automáticamente
    setIsUploading(true);
    setProgress(0);

    // Calcular incremento por paso para completar en 2.2 segundos
    // 2200ms / 11 pasos = 200ms por paso con incrementos de 10% cada vez
    let currentProgress = 0;
    const step = 10; // 10% por paso
    const stepDuration = 200; // 200ms por paso (2200ms / 11 pasos)

    const interval = setInterval(() => {
      currentProgress += step;
      if (currentProgress >= 100) {
        clearInterval(interval);
        setProgress(100);

        // Mostrar formulario de email después de completar la barra de progreso
        setIsUploading(false);
        setShowEmailForm(true);
      } else {
        setProgress(currentProgress);
      }
    }, stepDuration);

    // Esperar a que se complete la barra de progreso
    await new Promise(resolve => setTimeout(resolve, 2200));
  };

  const handleBack = () => {
    setShowEmailForm(false);
    setUploadedFileName('');
  };

  const openFileDialog = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <Card
      className={`mb-8 transition-all duration-300 ${
        isDragOver
          ? 'border-primary bg-primary/5 scale-[1.02]'
          : 'border-border hover:border-primary/50'
      }`}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={openFileDialog}
    >
      <CardHeader>
        <CardTitle className="text-xl flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Sube tu factura de luz
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Área de arrastre y soltado */}
          {!showEmailForm ? (
            <div className="relative">
              <div className="flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors hover:bg-muted/20">
                <AnimatePresence>
                  {isUploading ? (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="flex flex-col items-center"
                    >
                      <div className="mb-4">
                        <motion.div
                          animate={{
                            rotate: 360,
                            scale: [1, 1.1, 1],
                          }}
                          transition={{
                            repeat: Infinity,
                            duration: 1.5,
                            ease: "easeInOut",
                          }}
                          className="text-primary"
                        >
                          <FileText className="h-12 w-12" />
                        </motion.div>
                      </div>
                      <p className="text-sm text-muted-foreground mb-4">
                        {fileName || 'Subiendo archivo...'}
                      </p>
                      <div className="w-full max-w-md">
                        <Progress value={progress} className="w-full" />
                        <p className="text-xs text-muted-foreground mt-2">{Math.round(progress)}%</p>
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="flex flex-col items-center"
                    >
                      <FileText className="h-12 w-12 text-muted-foreground mb-4" />
                      <p className="text-lg font-medium">
                        Arrastra aquí tu factura o picha para buscarla
                      </p>
                      <p className="text-sm text-muted-foreground mt-2">
                        Aceptas nuestra política de privacidad
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>

                {!isValidType && !isUploading && (
                  <div className="mt-4 p-3 bg-destructive/10 border border-destructive rounded-md">
                    <p className="text-sm text-destructive">
                      Este tipo de archivo no es seguro. Por razones de seguridad, no se permite la subida de este tipo de archivos.
                    </p>
                  </div>
                )}

                {file && isValidType && !isUploading && !showEmailForm && (
                  <div className="mt-4 p-3 bg-green-500/10 border border-green-500 rounded-md">
                    <p className="text-sm text-green-500">
                      Archivo "{fileName}" seleccionado correctamente
                    </p>
                  </div>
                )}
              </div>

              {/* Input oculto para selección de archivos */}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                className="hidden"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png"
              />
            </div>
          ) : (
            <EmailForm
              fileName={fileName}
              onEmailSubmit={handleEmailSubmit}
              onBack={handleBack}
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
}